// O painel: o outro modo do mesmo aplicativo.
//
// As seções do menu dependem do NÍVEL de quem entrou — mas isso é escolha de
// desenho, não de segurança. Quem forjasse o nível no navegador veria as
// telas vazias, porque quem filtra é a API em toda consulta.

import { useState } from "react";
import { NavLink, Outlet, useSearchParams } from "react-router-dom";

import { BarraSuperior } from "../correio/BarraSuperior";
import { useSessao } from "../sessao/contexto";
import { Icone, type NomeDeIcone } from "../ui/Icone";
import type { Nivel } from "../api/tipos";

interface Secao {
  caminho: string;
  rotulo: string;
  icone: NomeDeIcone;
  minimo: Nivel;
  /** Prevista: tela existe, recurso ainda não. Marcada para a tela dizer. */
  previsto?: boolean;
  /**
   * Rótulo para quem NÃO é super admin, quando a tela mostra menos para ele.
   * "Migração de painel" prometeria à revenda um WHM que ela não vai ver.
   */
  rotuloAbaixoDoSuper?: string;
}

const SECOES: Secao[] = [
  { caminho: "", rotulo: "Visão geral", icone: "Activity", minimo: "admin_dominio" },
  { caminho: "revendas", rotulo: "Revendas", icone: "Layers", minimo: "super_admin" },
  // A MARCA é da revenda: o gestor de revenda mexe na dele sozinho, sem
  // pedir a ninguém (pedido do Jhou, 31/10/2026). Abaixo disso não aparece —
  // admin de domínio não troca a marca que a revenda vende.
  { caminho: "marca", rotulo: "Marca", icone: "Tag", minimo: "revenda" },
  { caminho: "dominios", rotulo: "Domínios", icone: "Globe", minimo: "admin_dominio" },
  { caminho: "caixas", rotulo: "Caixas e encaminhadores", icone: "Users", minimo: "admin_dominio" },
  { caminho: "entregas", rotulo: "Fila e entregas", icone: "Send", minimo: "admin_dominio" },
  { caminho: "quarentena", rotulo: "Antispam e quarentena", icone: "ShieldAlert", minimo: "admin_dominio" },
  { caminho: "bloqueios", rotulo: "Bloqueios de login", icone: "Lock", minimo: "admin_dominio" },
  // ABUSOS E BLOQUEIO DE IP — super admin só.
  //
  // Separado de "Bloqueios de login" de propósito, e a diferença é de
  // ESCOPO: aquela tela mostra conta e IP barrados no login do WEBMAIL, e um
  // admin de domínio pode ver os do domínio dele. Esta mostra IPs que tentam
  // senha nas PORTAS DE E-MAIL, e bloquear um IP não pertence a revenda
  // nenhuma — um endereço pode ser a saída NAT de um escritório com caixas de
  // dois clientes diferentes.
  { caminho: "seguranca-ip", rotulo: "Abusos e bloqueio de IP", icone: "ShieldCheck", minimo: "super_admin" },
  { caminho: "auditoria", rotulo: "Segurança e auditoria", icone: "ScrollText", minimo: "admin_dominio" },
  // PREVISTAS NO MOCKUP E AINDA SEM API.
  //
  // Elas ficavam no menu para todo mundo, com a razão escrita aqui: "ver
  // 'ainda não' é melhor que procurar onde está". A revisão de UX de
  // 30/10/2026 pediu o contrário — item de menu que abre uma página sobre
  // um recurso que não existe.
  //
  // As duas razões valem, e a diferença é QUEM OLHA. Para quem constrói o
  // produto, saber o que vem é útil; para um revendedor, item morto no menu
  // é produto inacabado. Então elas continuam existindo e passam a aparecer
  // só para super admin — a roadmap fica com quem a executa.
  //
  // `migracao` é exceção: tem API, tem tela e está em uso.
  { caminho: "relay", rotulo: "Relay de saída", icone: "ArrowRightLeft", minimo: "super_admin", previsto: true },
  // A REVENDA VÊ SÓ O IMAP (pedido do Jhou, 31/10/2026): uma caixa por vez,
  // só as mensagens. WHM/cPanel e DirectAdmin continuam do super admin — a API
  // recusa o resto para ela, e a tela nem desenha.
  {
    caminho: "migracao", rotulo: "Migração de painel", icone: "Package", minimo: "revenda",
    rotuloAbaixoDoSuper: "Migração (IMAP)",
  },
  { caminho: "backups", rotulo: "Backups", icone: "RotateCcw", minimo: "super_admin", previsto: true },
  { caminho: "licenca", rotulo: "Licença", icone: "BadgeCheck", minimo: "super_admin", previsto: true },
  { caminho: "integracoes", rotulo: "API e integrações", icone: "Link2", minimo: "super_admin" },
];

export function PainelLayout() {
  const { podeVer, sessao } = useSessao();
  const [parametros] = useSearchParams();
  // Mesma gaveta do webmail: no celular o menu de doze seções não pode ser
  // uma coluna fixa, senão sobra uma faixa para as tabelas — que são largas.
  const [gaveta, setGaveta] = useState(false);

  return (
    // `overflow-hidden`: a mesma garantia do webmail (ver correio/Layout).
    // O `main` daqui já rola por dentro; isto impede que uma seção nova
    // faça o DOCUMENTO rolar atrás do invólucro de altura fixa, que é o que
    // deixa a faixa branca embaixo da barra lateral.
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-linha text-tinta">
      <BarraSuperior
        area="painel"
        recolher={() => setGaveta((g) => !g)}
        buscaInicial={parametros.get("q") ?? ""}
      />

      <div className="flex min-h-0 flex-1">
        {gaveta && (
          <button
            type="button"
            aria-label="Fechar menu"
            onClick={() => setGaveta(false)}
            className="fixed inset-0 z-30 bg-black/40 md:hidden"
          />
        )}
        <aside
          onClick={() => setGaveta(false)}
          className={[
            "flex flex-col gap-0.5 pt-3 pr-3 transition-transform md:transition-none",
            "fixed inset-y-0 left-0 z-40 w-64 overflow-y-auto bg-linha",
            gaveta ? "translate-x-0" : "-translate-x-full",
            "md:static md:z-auto md:w-60 md:flex-shrink-0 md:translate-x-0 md:overflow-visible",
          ].join(" ")}
        >
          {SECOES.filter((s) => podeVer(s.minimo)).map((s) => (
            <NavLink
              key={s.caminho || "visao"}
              to={"/painel/" + s.caminho}
              end={s.caminho === ""}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-r-full py-2 pl-6 pr-3 text-sm ${
                  isActive
                    ? "bg-acao-clara font-semibold text-acao-escura"
                    : "text-tinta-media hover:bg-linha"
                }`
              }
            >
              <Icone nome={s.icone} tamanho={17} />
              <span className="flex-1">
                {s.rotuloAbaixoDoSuper && !podeVer("super_admin") ? s.rotuloAbaixoDoSuper : s.rotulo}
              </span>
              {/* O selo diz a verdade ANTES do clique. Sem ele, a pessoa
                  abre a tela esperando o recurso e encontra um aviso. */}
              {s.previsto && (
                <span className="rounded-full bg-linha px-1.5 py-0.5 text-[10px] text-tinta-fraca">
                  em breve
                </span>
              )}
            </NavLink>
          ))}

          <div className="mx-3 mt-auto mb-3 rounded-lg bg-tinta p-3 text-xs text-white/80">
            <p className="mb-1 flex items-center gap-2">
              <Icone nome="BadgeCheck" tamanho={14} className="text-acao-clara" />
              {sessao?.nivel === "super_admin"
                ? "Super administrador"
                : sessao?.nivel === "revenda"
                  ? "Revenda"
                  : "Administrador de domínio"}
            </p>
            <p className="text-white/50">{sessao?.caixa}</p>
          </div>
        </aside>

        <main className="min-w-0 flex-1 overflow-y-auto rounded-tl-2xl bg-white p-3 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
